import { z } from 'zod';
import { slugSchema } from './client.js';
import { formSelectorsSchema } from './form.js';

/**
 * The form plugins the site-side inventory knows how to describe. `none` means the
 * plugin is active but found no supported form plugin to read.
 *
 * @since 1.0.0
 */
export const INVENTORY_FORM_PLUGINS = ['gravityforms', 'none'] as const;

export const inventoryFormPluginSchema = z.enum(INVENTORY_FORM_PLUGINS);

export type InventoryFormPlugin = z.infer<typeof inventoryFormPluginSchema>;

/**
 * What the site reports about itself alongside its forms.
 *
 * @since 1.0.0
 */
export const siteInventorySiteSchema = z.object({
    url: z.string(),
    name: z.string(),
    wordpress: z.string(),
    plugin: z.string(),
    formPlugin: inventoryFormPluginSchema,
    formPluginVersion: z.string().nullable().default(null),
    qcModeEnabled: z.boolean().default(false),
});

export type SiteInventorySite = z.infer<typeof siteInventorySiteSchema>;

/**
 * One form as the site describes it: where it is embedded, who receives its
 * notifications, and the selectors a canary needs to drive it. `reasons` explains a
 * form the site reports as not canaryable.
 *
 * @since 1.0.0
 */
export const siteInventoryFormSchema = z.object({
    id: z.coerce.string(),
    slug: slugSchema,
    name: z.string(),
    isActive: z.boolean().default(true),
    isCanaryable: z.boolean().default(false),
    reasons: z.array(z.string()).default([]),
    pagePaths: z.array(z.string()),
    expectedRecipients: z.array(z.email()),
    confirmationType: z.enum(['message', 'page', 'redirect', 'none']).default('none'),
    honeypotAction: z.enum(['spam', 'abort', 'none']).default('none'),
    hasCaptcha: z.boolean().default(false),
    hasFeeds: z.boolean().default(false),
    selectors: formSelectorsSchema,
});

export type SiteInventoryForm = z.infer<typeof siteInventoryFormSchema>;

/**
 * The response of the site's inventory endpoint, which the dashboard pulls to
 * register forms without anyone typing selectors by hand.
 *
 * @since 1.0.0
 */
export const siteInventorySchema = z.object({
    site: siteInventorySiteSchema,
    forms: z.array(siteInventoryFormSchema),
});

export type SiteInventory = z.infer<typeof siteInventorySchema>;

/**
 * A form the sync left alone, and why.
 *
 * @since 1.0.0
 */
export const syncSkippedFormSchema = z.object({
    slug: z.string(),
    name: z.string(),
    reasons: z.array(z.string()),
});

export type SyncSkippedForm = z.infer<typeof syncSkippedFormSchema>;

/**
 * What one pull of a site's inventory did to the client's registered forms.
 *
 * @since 1.0.0
 */
export const syncFormsResultSchema = z.object({
    discovered: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.array(syncSkippedFormSchema),
});

export type SyncFormsResult = z.infer<typeof syncFormsResultSchema>;
